import React, {ChangeEvent, FC, SyntheticEvent, useEffect, useRef, useState} from 'react';

import avatar from 'assets/images/svg/avatar.svg';

import {createCommentManager} from '../../../../di';
import {useErrorCallback} from '../../../../hooks';
import {cn} from '../../../../utils';
import {ArrowIcon, Image} from '../../../common';
import styles from './CommentForm.module.scss';

interface Props {
  projectUuid: string;
  parentUuid?: string;
  onCreated?: () => void;
  autoFocus?: boolean;
}

const MAX_LENGTH = 500;

const CommentForm: FC<Props> = ({projectUuid, parentUuid, onCreated, autoFocus}) => {
  const [sent, setSent] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const handleError = useErrorCallback();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const validationTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 300);
    }
  }, [autoFocus]);

  const runValidationWithDebounce = (value: string) => {
    if (validationTimeoutRef.current !== null) {
      window.clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = window.setTimeout(() => {
      const trimmed = value.trim();

      if (!value.length) {
        setError(null);
        return;
      }

      if (!trimmed) {
        setError('Комментарий не может состоять только из пробелов');
        return;
      }

      setError(null);
    }, 250);
  };

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }

    if (value.length > MAX_LENGTH) {
      setError(`Максимальная длина комментария — ${MAX_LENGTH} символов`);
      setNewComment(value.slice(0, MAX_LENGTH));
      return;
    }

    setSent(false);
    setNewComment(value);
    runValidationWithDebounce(value);
  };

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    const trimmed = newComment.trim();

    if (!trimmed) {
      setError('Комментарий не может быть пустым');
      return;
    }

    if (trimmed.length > MAX_LENGTH) {
      setError(`Максимальная длина комментария — ${MAX_LENGTH} символов`);
      return;
    }

    setSent(false);

    try {
      const manager = await createCommentManager();
      await manager.create(projectUuid, trimmed, parentUuid);
      setNewComment('');
      setError(null);
      setSent(true);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      onCreated?.();
    } catch (error: any) {
      await handleError(error);
    }
  };

  const hasError = !!error;
  const isEmpty = !newComment.trim();
  const sendInactive = hasError || isEmpty;

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <Image className={styles.avatar} src={avatar} alt={'avatar'} />

      <div className={styles.mainColumn}>
        <div className={styles.inputWrapper}>
          <textarea
            value={newComment}
            ref={textareaRef}
            onChange={handleInputChange}
            placeholder="Ваш комментарий"
            className={styles.input}
          />

          <button type="submit" className={styles.sendButton} disabled={sendInactive}>
            <span
              className={cn(
                styles.sendIconWrap,
                sendInactive ? styles.sendIconWrapInactive : styles.sendIconWrapActive
              )}
            >
              <ArrowIcon
                direction="up"
                size={24}
                color={sendInactive ? 'var(--gray-color-3)' : 'var(--white-color)'}
                title="Отправить"
              />
            </span>
          </button>
        </div>

        <div className={styles.metaRow}>
          {error && <span className={styles.error}>{error}</span>}
          {sent && !hasError && <span className={styles.sent}>Отправлено!</span>}

          <span className={styles.counter}>
            {newComment.length}/{MAX_LENGTH}
          </span>
        </div>
      </div>
    </form>
  );
};

export default CommentForm;
